export type PollStatus = "atanmis" | "atanmamis" | "taslak" | "arsiv";

export type AttendeeStatus = "katildi" | "katilmadi";

/** Backend SurveyStatus enum - GET /api/admin/surveys response */
export enum SurveyStatus {
  /** Taslak - Draft survey, not yet published */
  DRAFT = 0,
  /** Atanmış - Survey assigned to at least one education */
  ASSIGNED = 1,
  /** Atanmamış - Survey not assigned to any education */
  UNASSIGNED = 2,
  /** Arşiv - Archived survey */
  ARCHIVE = 3,
}

// --- API Types (GET /api/admin/surveys - Swagger) ---

/** Sıralama alanı - Swagger GET /api/admin/surveys */
export type SurveySortBy = "name" | "publishDate" | "participatedPercentage" | "remainingTime";

/** Sıralama yönü - Swagger: asc (artan), desc (azalan) */
export type SurveySortOrder = "asc" | "desc";

/** Query params - Backend page, limit, status, educationIds, sortBy, sortOrder */
export interface AdminSurveysQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: SurveyStatus;
  educationIds?: string[];
  sortBy?: SurveySortBy;
  sortOrder?: SurveySortOrder;
}

/** Eğitim temel bilgisi - Backend EducationBasicResDto */
export interface EducationBasicResDto {
  id: string;
  name: string;
}

/** Backend SurveyQuestionOptionResDto */
export interface SurveyQuestionOptionResDto {
  id: string;
  questionID: string;
  optionText: string;
  order?: number | null;
  createdAt: string;
  updatedAt: string;
}

/** Backend SurveyQuestionResDto */
export interface SurveyQuestionResDto {
  id: string;
  surveyID: string;
  type: SurveyQuestionType; // 1=TEXT, 2=SINGLE_CHOICE, 3=MULTIPLE_CHOICE, 4=DROPDOWN, 5=RATING, 6=SCALE
  questionText: string;
  isRequired: boolean;
  order?: number | null;
  options: SurveyQuestionOptionResDto[];
  createdAt: string;
  updatedAt: string;
}

/** Tekil survey item - Backend SurveyResDto */
export interface SurveyResDto {
  id: string;
  name?: string;
  description?: string | null;
  createdAt: string;
  updatedAt: string;
  questions: SurveyQuestionResDto[];
  relatedEducation: EducationBasicResDto | null;
  status?: SurveyStatus;
  startDate?: string | null;
  endDate?: string | null;
  /** Toplam atanan katılımcı sayısı */
  totalEnrolledCount?: number;
  /** Anketi tamamlayan katılımcı sayısı */
  participatedPercentage?: number;
  participatedCount?: number;
}

/** API response - Backend SurveyResDtoPaginatedResponse */
export interface SurveyResDtoPaginatedResponse {
  success?: boolean;
  status?: number;
  data: SurveyResDto[];
  count: number;
  message?: string;
}

export interface Poll {
  id: string;
  pollName: string;
  publishDate: string;
  relatedEducation: string;
  participation: {
    score: number;
    total: number;
    percentage: number;
    participatedCount: number;
  };
  remainingTime: string;
  status: PollStatus;
  createdAt: string;
  participatedCount: number;
  updatedAt: string;
}

export interface PollFilters {
  search?: string;
  status?: PollStatus;
  /** Eğitim ID'leri (çoklu seçim) */
  educationIds?: string[];
  /** Sıralama alanı (name, publishDate, participatedPercentage, remainingTime) */
  sortBy?: SurveySortBy;
  /** Sıralama yönü (asc, desc) */
  sortOrder?: SurveySortOrder;
}

export interface PollQuestion {
  id: string;
  questionText: string;
  questionType: "multiple_choice" | "text" | "rating" | "single_choice" | "dropdown" | "scale";
  options?: string[];
  required: boolean;
}

export interface PollDetail extends Poll {
  description: string;
  startDate: string;
  endDate: string;
  questions: PollQuestion[];
  totalAttendees: number;
  completedAttendees: number;
}

/** GET /api/admin/surveys/:surveyId/attempts - query params */
export interface GetAttendeesParams {
  search?: string;
  sortBy?: "name" | "answerRate";
  sortOrder?: "asc" | "desc";
  completed?: boolean;
  answerRateMin?: number;
  answerRateMax?: number;
}

export interface PollAttendee {
  id: string;
  name: string;
  title: string;
  avatar?: string;
  status: AttendeeStatus;
  answeredCount: number;
  totalQuestions: number;
  percentage: number;
  /**
   * Anket birden fazla eğitime atanabildiği için yanıtın hangi eğitim
   * bağlamında verildiğini göstermek için eğitim bilgisi de aktarılır.
   */
  educationId?: string | null;
  educationName?: string | null;
}

/** Backend SurveyAnswerDetailResDto - GET survey-attempts/{attemptId}/answers */
export interface SurveyAnswerDetailResDto {
  id: string;
  questionID: string;
  questionText: string;
  selectedOption?: { id: string; optionText: string };
  selectedOptions?: Array<{ id: string; optionText: string }>;
  textValue?: string;
  createdAt: string;
  updatedAt: string;
}

/** Backend SurveyAttemptDetailResDto - GET survey-attempts/{attemptId}/answers */
export interface SurveyAttemptDetailResDto {
  id: string;
  userID: string;
  user?: {
    firstName?: string;
    lastName?: string;
    department?: { id: string; name: string };
  };
  startedAt?: string;
  finishedAt?: string;
  answers: SurveyAnswerDetailResDto[];
  createdAt: string;
  updatedAt: string;
}

// --- Create Survey (POST /api/admin/surveys) ---

/** Backend soru tipi enum - CreateSurveyQuestionDto.type */
export enum SurveyQuestionType {
  TEXT = 1,
  SINGLE_CHOICE = 2,
  MULTIPLE_CHOICE = 3,
  DROPDOWN = 4,
  RATING = 5,
  SCALE = 6,
}

/** Backend CreateSurveyQuestionOptionDto */
export interface CreateSurveyQuestionOptionDto {
  optionText: string;
  order: number;
}

/** Backend CreateSurveyQuestionDto */
export interface CreateSurveyQuestionDto {
  type: SurveyQuestionType;
  questionText: string;
  isRequired: boolean;
  order: number;
  imageFileID?: string;
  chapterID?: string;
  objectiveID?: string;
  videoMinute?: string;
  options?: CreateSurveyQuestionOptionDto[];
}

/** Backend CreateSurveyDto */
export interface CreateSurveyDto {
  name: string;
  description: string;
  educationID: string;
  startDate?: string;
  endDate?: string;
  /** 0=DRAFT, 1=ASSIGNED, 2=UNASSIGNED, 3=ARCHIVE */
  status?: SurveyStatus;
  questions: CreateSurveyQuestionDto[];
}

/** Backend UpdateSurveyDto - PATCH /api/admin/surveys/{id} */
export interface UpdateSurveyDto {
  name?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  status?: SurveyStatus; // 0=DRAFT, 1=ASSIGNED, 2=UNASSIGNED, 3=ARCHIVE
  questions?: CreateSurveyQuestionDto[];
}
